import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen,
  Check,
  Crown,
  Loader2,
  ArrowLeft,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { analytics } from '../utils/analytics';
import { API_URL } from '../config/api';

export default function Subscription() {
  const { accessToken, profile, refreshProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      setIsPolling(false);
      return;
    }

    setIsPolling(true);

    try {
      const response = await fetch(`${API_URL}/api/subscription/status/${sid}`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });

      if (!response.ok) throw new Error('Failed to check payment status');

      const data = await response.json();

      if (data.payment_status === 'paid') {
        analytics.checkoutSuccess(sid);
        toast.success('Payment successful! You now have unlimited access.');
        await refreshProfile();
        setIsPolling(false);
        navigate('/dashboard');
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setIsPolling(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    }
  };

  const handleSubscribe = async () => {
    setIsLoading(true);
    analytics.upgradeButtonClicked('subscription_page');

    try {
      const response = await fetch(`${API_URL}/api/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout session');
      }

      const data = await response.json();

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (error) {
      toast.error(error.message || 'Failed to start checkout');
      setIsLoading(false);
    }
  };

  if (profile?.is_subscribed) {
    return (
      <div className="min-h-screen bg-white">
        <nav className="border-b-2 border-border">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <Link to="/" className="flex items-center gap-2 w-fit">
              <div className="w-10 h-10 bg-primary flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <span className="font-heading font-black text-lg tracking-tight">CALC AI</span>
            </Link>
          </div>
        </nav>
        <main className="max-w-md mx-auto px-6 py-16 text-center">
          <div className="w-16 h-16 bg-primary mx-auto mb-6 flex items-center justify-center">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading font-black text-2xl mb-4">YOU'RE A PRO!</h1>
          <p className="text-muted-foreground mb-8">
            You have unlimited access to all study tools.
          </p>
          <Link to="/dashboard">
            <Button className="h-12 px-8 font-bold uppercase">
              Go to Dashboard
            </Button>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b-2 border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-black text-lg tracking-tight">CALC AI</span>
          </Link>
          <Link to="/dashboard">
            <Button variant="outline" className="h-10 border-2">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {isPolling ? (
          <motion.div
            className="text-center py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
            <h2 className="font-heading font-bold text-xl mb-2">Processing Payment...</h2>
            <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
          </motion.div>
        ) : (
          <>
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                UPGRADE YOUR ACCOUNT
              </span>
              <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight mt-2">
                UNLOCK UNLIMITED STUDY TOOLS
              </h1>
              <p className="text-muted-foreground mt-4 max-w-xl mx-auto">
                Get unlimited access to AI-generated explanations, quizzes, exams, and flashcards.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              {/* Free Tier */}
              <motion.div
                className="border-2 border-border p-6"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h3 className="font-heading font-bold mb-2">FREE</h3>
                <p className="text-4xl font-heading font-black mb-6">$0</p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary mt-0.5" />
                    5 generations per day
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary mt-0.5" />
                    Resets daily
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary mt-0.5" />
                    All study tool types
                  </li>
                </ul>
                <Button
                  variant="outline"
                  className="w-full h-12 border-2"
                  disabled
                >
                  Current Plan
                </Button>
              </motion.div>

              {/* Pro Tier */}
              <motion.div
                className="border-2 border-primary bg-primary/5 p-6 relative overflow-hidden"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="absolute top-0 right-0 bg-primary text-white text-xs font-bold px-3 py-1">
                  RECOMMENDED
                </div>
                <h3 className="font-heading font-bold mb-2 flex items-center gap-2">
                  PRO <Crown className="w-4 h-4 text-primary" />
                </h3>
                <p className="text-4xl font-heading font-black mb-1">$7.99</p>
                <p className="text-sm text-muted-foreground mb-6">per month</p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2 text-sm">
                    <Sparkles className="w-4 h-4 text-primary mt-0.5" />
                    <strong>Unlimited</strong> study tool generations
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary mt-0.5" />
                    Priority AI processing
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary mt-0.5" />
                    Save all study sessions
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary mt-0.5" />
                    Cancel anytime
                  </li>
                </ul>
                <Button
                  className="w-full h-12 font-bold uppercase"
                  onClick={handleSubscribe}
                  disabled={isLoading}
                  data-testid="subscribe-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Upgrade to Pro'
                  )}
                </Button>
              </motion.div>
            </div>

            <p className="text-center text-sm text-muted-foreground mt-8">
              Secure payment powered by Stripe. Cancel anytime.
            </p>
          </>
        )}
        {/* Footer */}
        <footer className="border-t-2 border-border py-8 px-6 bg-white">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading font-bold text-sm">CALC AI</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © Powered by FHYAS LLC.
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}
